import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import service from '../service';
import type { Venue } from '../types/venue';
import type { UpdateVenuePayload } from '../types/api';

export interface UpdateVenueVariables {
  venueId: string;
  payload: UpdateVenuePayload;
}

export type UpdateVenueResult = Venue;

/**
 * useUpdateVenue
 *
 * Mutation hook for updating a venue.
 *
 * @example
 * const { mutateAsync: updateVenue, isPending, error } = useUpdateVenue();
 *
 * await updateVenue({
 *   venueId: '123',
 *   payload: {
 *     featuredAssetId: 'asset-id-1',
 *   },
 * });
 */
export function useUpdateVenue(
  options?: UseMutationOptions<UpdateVenueResult, Error, UpdateVenueVariables>,
): UseMutationResult<UpdateVenueResult, Error, UpdateVenueVariables> {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<UpdateVenueResult, Error, UpdateVenueVariables>({
    mutationKey: ['venue', 'update'],
    mutationFn: async ({ venueId, payload }) => {
      return service.updateVenue(venueId, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate venue queries to refresh data including featured image
      queryClient.invalidateQueries({ queryKey: ['venue', variables.venueId] });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      // Also invalidate any venue queries with different include params
      queryClient.invalidateQueries({
        queryKey: ['venue'],
        exact: false,
      });
      onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
